import {
  CompensationMethodData,
  isEnsoEngraving,
  isVariantInStock,
} from "@redotech/redo-customer-portal-shared/flow/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ExchangeVariant,
  ReturnProduct,
} from "@redotech/redo-model/draft-return/draft-return-items";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import * as questions from "../choose-items.module.css";
import { EnsoEngravingFee } from "./enso-engraving";
import {
  ExchangeProduct,
  ExchangeProductVariant,
  VariantBubbles,
} from "./variant-bubbles";
import * as variantExchange from "./variant-exchange.module.css";

export type ChooseVariantFooter = React.MemoExoticComponent<
  ({
    enabled,
    onSubmit,
    onBack,
    pending,
  }: {
    enabled: boolean;
    onSubmit: () => void;
    onBack: () => void;
    pending?: boolean;
  }) => JSX.Element
>;

export const VariantExchange = memo(function VariantExchange({
  settings,
  product,
  onBack,
  onVariantVisible,
  headerText = "Select exchange options:",
  footer: Footer,
  chooseVariant,
  submitPending: pending = false,
  ignoreStock = false,
  multipleChoiceAnswers,
  returnType,
  submitItem,
}: {
  settings: ReturnAppSettings;
  product: ReturnProduct;
  onBack: () => void;
  onVariantVisible: (variant: ExchangeVariant | undefined) => void;
  headerText?: string;
  footer: ChooseVariantFooter;
  chooseVariant: (variant: ExchangeVariant) => void;
  submitPending?: boolean;
  ignoreStock?: boolean;
  multipleChoiceAnswers?: string[];
  returnType?: ReturnTypeEnum;
  submitItem: (data: CompensationMethodData) => void;
}) {
  const { formatCurrency } = useContext(CurrencyContext);

  const applyPriceDifference = useMemo(() => {
    if (!settings.differentPricedVariantExchanges) {
      return false;
    }
    if (!settings.variantExchangeProductTags.length) {
      return true;
    }
    return product.productTags.some((tag) =>
      settings.variantExchangeProductTags.includes(tag),
    );
  }, [settings, product]);

  const [selectedVariant, setSelectedVariant] =
    useState<ExchangeProductVariant | null>(null);

  const productData = useMemo(() => {
    return getExchangeProduct(product, settings, returnType);
  }, [product, settings, returnType]);

  useEffect(() => {
    if (selectedVariant) {
      onVariantVisible(
        product.variantData.variants.find(
          (variant) => variant.id === selectedVariant.id,
        ),
      );
    }
  }, [selectedVariant, onVariantVisible, product]);
  useEffect(() => {
    return () => onVariantVisible(undefined);
  }, [onVariantVisible]);

  const handleSelection = () => {
    if (selectedVariant) {
      chooseVariant(
        product.variantData.variants.find(
          (variant) => variant.id === selectedVariant.id,
        )!,
      );
    }
  };

  const [oferringEngraving, setOfferingEngraving] = useState(false);

  if (oferringEngraving) {
    return (
      <EnsoEngravingFee
        multipleChoiceAnswers={multipleChoiceAnswers ?? []}
        returnType={returnType}
        selectedVariant={
          product.variantData.variants.find(
            (variant) => variant.id === selectedVariant?.id,
          ) ?? null
        }
        submitItem={submitItem}
      />
    );
  }

  return (
    <Flex dir="column" grow={1}>
      <div className={variantExchange.cardContent}>
        <div className={variantExchange.topContent}>
          <div>
            <h2 className={variantExchange.variantScreenTitle}>{headerText}</h2>
            <VariantBubbles
              ignoreStock={ignoreStock}
              productData={productData}
              returnType={returnType}
              setSelectedVariant={setSelectedVariant}
              settings={settings}
              showErrors={false}
            />
            {!!applyPriceDifference && !!selectedVariant && (
              <div>Price: {formatCurrency(selectedVariant?.price)}</div>
            )}
          </div>
        </div>
      </div>
      <hr className={questions.line} />
      <div className={variantExchange.buttonWrapper}>
        <Footer
          enabled={!!selectedVariant}
          onBack={onBack}
          onSubmit={() => {
            if (
              isEnsoEngraving(
                settings,
                product,
                returnType || ReturnTypeEnum.CLAIM,
              )
            ) {
              setOfferingEngraving(true);
            } else {
              handleSelection();
            }
          }}
          pending={pending}
        />
      </div>
    </Flex>
  );
});

// TODO (jaden): this is a very gross mapping, but by Oct/14/2024 I will convert exchage groups to use a standard type so that no mapping is needed
const getExchangeProduct = (
  product: ReturnProduct,
  settings: ReturnAppSettings,
  returnType: ReturnTypeEnum | undefined,
): ExchangeProduct => {
  return {
    id: product.id,
    availableForSale: true,
    title: product.title,
    vendor: "",
    productType: product.productType,
    totalInventory: 1,
    tags: product.productTags,
    image: product.image,
    variants: product.variantData.variants.map((variant) =>
      getExchangeProductVariant(variant, settings, returnType),
    ),
    options: product.variantData.options.map((option) => {
      return { id: option.name, name: option.name, values: option.values };
    }),
    publishedAt: null,
    price: product.price,
    description: "",
    collectionIds: [],
  };
};

const getExchangeProductVariant = (
  variant: ExchangeVariant,
  settings: ReturnAppSettings,
  returnType: ReturnTypeEnum | undefined,
): ExchangeProductVariant => {
  return {
    title: variant.title,
    productTitle: "",
    productId: variant?.productId ?? "",
    id: variant.id,
    price: variant.price,
    option1: variant.selectedOptions?.[0]?.value,
    option2: variant.selectedOptions?.[1]?.value,
    option3: variant.selectedOptions?.[2]?.value,
    requiresShipping: true,
    quantityAvaliable: variant.inventoryQuantity,
    currentlyNotInStock: !isVariantInStock({
      settings,
      returnType,
      inventoryPolicy: variant.inventoryPolicy,
      inventoryTracked: variant.inventoryTracked,
      inventoryQuantity: variant.inventoryQuantity,
    }),
    selectedOptions: variant.selectedOptions,
    image: variant.image || null,
    sku: variant.sku || null,
    availableForSale: isVariantInStock({
      settings,
      returnType,
      inventoryPolicy: variant.inventoryPolicy,
      inventoryTracked: variant.inventoryTracked,
      inventoryQuantity: variant.inventoryQuantity,
    }),
  };
};
